import React, { useState, useEffect, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import { FirebaseAnalytics } from "@ionic-native/firebase-analytics";

import PageLayout from "../../layouts/PageLayout";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Title from "components/Title";
import Button from "components/Button";
import api from "../../api";
import { tryCatch, getReasonString } from "../../utils";

import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";

import PlanSelectionForm from "../onboarding/components/PlanSelectionForm";
import {
  getDisplayablePlanPrices,
  type ConfigPrice,
} from "../onboarding/components/planSelectionPricing";

interface Props {
  reason?: string;
}

export const SubscribeContainer: React.FC<Props> = ({ reason }) => {
  const { adapterId } = useParams<{ adapterId: string }>();
  const history = useHistory();
  const { t } = useTranslation("subscribe");
  const { t: tOnboarding } = useTranslation("onboarding");
  const [stripeKey, setStripeKey] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<ReturnType<
    typeof loadStripe
  > | null>(null);
  const [clientSecret, setClientSecret] = useState<string>("");
  const [formStep, setFormStep] = useState<number>(1);
  const [prices, setPrices] = useState<ConfigPrice[]>([]);
  const [selectedPrice, setSelectedPrice] = useState<ConfigPrice | null>(null);
  const { adapter } = useSelector((state: RootState) => ({
    adapter: state.adapter.adapter,
  }));
  const reasonString = getReasonString(reason);

  const displayablePrices = useMemo(
    () => getDisplayablePlanPrices(prices, { recurringOnly: true, reason }),
    [prices, reason]
  );

  useEffect(() => {
    FirebaseAnalytics.logEvent("init_page", { page: "subscribe" });
    getConfiguration();
  }, []);

  useEffect(() => {
    if (stripeKey) {
      setStripePromise(loadStripe(stripeKey));
    }
  }, [stripeKey]);

  const getConfiguration = async () => {
    const configRes = await tryCatch(api.subscription.getConfig());
    if (configRes.error) {
      console.warn(configRes.error.message);
    } else {
      if (configRes.result.data.publishable_key) {
        setStripeKey(configRes.result.data.publishable_key);
      }
      if (configRes.result.data.prices) {
        setPrices(configRes.result.data.prices);
      }
    }
  };

  const getCheckoutSession = async (price_id: string) => {
    setClientSecret("");

    const targetAdapterId = adapterId ?? adapter?.id;
    if (!targetAdapterId) {
      console.warn("Subscribe: missing adapter id for checkout session");
      return;
    }

    const apiResult = await tryCatch(
      api.subscription.createSubscriptionCheckoutSession(
        targetAdapterId,
        price_id
      )
    );

    if (apiResult.error) {
      console.warn(apiResult.error.message);
    } else {
      const client_secret = apiResult.result.data.client_secret;
      setClientSecret(client_secret);
    }
  };

  const handleContinueToCheckout = () => {
    if (!selectedPrice?.recurring || !stripePromise) {
      return;
    }
    setFormStep(2);
    getCheckoutSession(selectedPrice.id);
  };

  return (
    <PageLayout back={history.goBack}>
      <Title title={t("title")} className="mb-2" />
      {reason && (
        <div
          style={{ padding: "20px", display: "flex", justifyContent: "center" }}
        >
          <p style={{ whiteSpace: "pre-line", width: "80%" }}>
            {t(reasonString)}
          </p>
        </div>
      )}
      {prices.length > 0 && stripePromise && (
        <div>
          {formStep === 1 && (
            <div style={styles.formContainer}>
              <PlanSelectionForm
                prices={displayablePrices}
                allPricesForSavings={prices}
                selectedPrice={selectedPrice}
                onSelectPrice={setSelectedPrice}
                renewalOption="renewLater"
                showRenewalToggle={false}
              />
              <div style={styles.continueRow}>
                <Button
                  data-testid="submit"
                  className="text-center mr-3 w-full"
                  size="small"
                  type="button"
                  style={{ maxWidth: "320px" }}
                  onClick={handleContinueToCheckout}
                  disabled={!selectedPrice}
                >
                  {t("subscribe")}
                </Button>
              </div>
              {selectedPrice && (
                <div style={styles.billingAddendum}>
                  <p>{tOnboarding("choosePlan.billingAddendum1")}</p>
                  <p>{tOnboarding("choosePlan.billingAddendum2")}</p>
                </div>
              )}
            </div>
          )}
          {formStep === 2 && clientSecret && stripePromise && (
            <div style={styles.formContainer}>
              <div style={{ alignSelf: "flex-start" }}>
                <Button
                  data-testid="submit"
                  className="text-center mr-3"
                  size="small"
                  type="button"
                  disabled={false}
                  onClick={() => {
                    setFormStep(1);
                    setClientSecret("");
                  }}
                >
                  Back
                </Button>
              </div>
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={{ clientSecret }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          )}
        </div>
      )}
    </PageLayout>
  );
};

const styles = {
  formContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column" as "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
    gap: "10px",
  },
  continueRow: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "8px",
  },
  billingAddendum: {
    width: "100%",
    maxWidth: "92%",
    marginTop: "8px",
    fontSize: "13px",
    color: "rgba(26,26,26,0.75)",
    lineHeight: 1.5,
    display: "flex",
    flexDirection: "column" as const,
    gap: "4px",
  },
};

export default SubscribeContainer;
